import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import Avatar from '@mui/material/Avatar';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import { Search, X } from 'lucide-react';

// Presentational: searchable checkbox list of students + a "select all"
// shortcut. Never silently caps the selection — "select all" checks every
// student even past maxRecipients, and the parent (BroadcastComposer) is
// responsible for disabling Send until the tutor manually deselects back
// down to the cap.
const RecipientPicker = ({ students, selectedIds, onToggle, onSelectAll, onClearAll, maxRecipients, loading }) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');

  const isDark = theme?.mode === 'dark';
  const primaryColor = theme?.colors?.primary_light || '#1a4275';
  const accentColor = theme?.colors?.button_light || '#fdbf00';
  const borderCol = isDark ? 'rgba(51, 65, 85, 0.4)' : '#e2e8f0';
  const textPrimary = isDark ? '#f8fafc' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';

  const filteredStudents = students.filter(s =>
    s.fullname.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const allSelected = students.length > 0 && students.every(s => selectedIds.includes(s.id));

  const handleSelectAllToggle = () => {
    if (allSelected) {
      onClearAll();
    } else {
      onSelectAll();
    }
  };

  const renderAvatar = (student) => {
    const hasPhoto = student.profileimageurl &&
      !student.profileimageurl.includes('f1.png') &&
      !student.profileimageurl.includes('f1.svg') &&
      !student.profileimageurl.includes('f2.png') &&
      !student.profileimageurl.includes('f2.svg') &&
      !student.profileimageurl.includes('default');

    if (!hasPhoto) {
      const initials = student.fullname
        ? student.fullname.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
        : '?';

      return (
        <Avatar
          style={{
            width: 36,
            height: 36,
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 'bold',
          }}
        >
          {initials}
        </Avatar>
      );
    }

    return <Avatar src={student.profileimageurl} alt={student.fullname} style={{ width: 36, height: 36 }} />;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-3 pb-2">
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Buscar alumno..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-10 py-2.5 rounded-full text-lg font-medium focus:outline-none transition-all duration-300 ${
              isDark
                ? 'bg-slate-800/80 text-white placeholder-slate-500 focus:bg-slate-800 focus:ring-1 focus:ring-slate-600'
                : 'bg-slate-100 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-slate-300'
            }`}
          />
          <Search size={18} className="absolute left-3.5 pointer-events-none" style={{ color: '#94a3b8' }} />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3.5 p-1 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      <div
        className="mx-4 mb-2 flex items-center justify-between px-3.5 py-2.5 rounded-xl border cursor-pointer"
        style={{ borderColor: borderCol }}
        onClick={handleSelectAllToggle}
      >
        <span className="text-base font-semibold" style={{ color: textPrimary }}>
          Seleccionar todos ({students.length})
        </span>
        <Checkbox
          checked={allSelected}
          indeterminate={!allSelected && selectedIds.length > 0}
          onChange={handleSelectAllToggle}
          onClick={(e) => e.stopPropagation()}
          style={{ color: accentColor }}
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 custom-scrollbar space-y-1">
        {loading ? (
          <div className="flex flex-col justify-center items-center py-10 space-y-2">
            <CircularProgress size={26} style={{ color: primaryColor }} />
            <span className="text-base text-slate-400">Cargando alumnos...</span>
          </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center text-base text-slate-400 py-10 italic">
            No hay alumnos disponibles.
          </div>
        ) : (
          filteredStudents.map(student => {
            const checked = selectedIds.includes(student.id);
            return (
              <div
                key={student.id}
                className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-100/60 dark:hover:bg-slate-800/40 cursor-pointer transition-colors"
                onClick={() => onToggle(student.id)}
              >
                {/* stopPropagation is required: React maps a checkbox's onChange
                    to the native click, and that same click also bubbles to the
                    row's onClick. Without this, a direct click on the checkbox
                    fires onToggle twice — and since the parent uses a functional
                    state update, the second call undoes the first, leaving the
                    checkbox dead while the row still works. */}
                <Checkbox
                  checked={checked}
                  onChange={() => onToggle(student.id)}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: accentColor }}
                />
                {renderAvatar(student)}
                <span className="text-lg font-medium truncate" style={{ color: textPrimary }}>
                  {student.fullname}
                </span>
              </div>
            );
          })
        )}
      </div>

      <div className="px-4 pb-3 pt-1 text-base font-semibold text-center" style={{ color: textSecondary }}>
        {selectedIds.length} seleccionados / {maxRecipients} máx
      </div>
    </div>
  );
};

export default RecipientPicker;
